import React, { useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import './Input.css';

/**
 * Componente Input moderno seguindo a identidade visual Mexx
 * Suporta múltiplos tipos, estados e validação
 */
const Input = forwardRef(({
  type = 'text',
  label = '',
  placeholder = '',
  value = '',
  defaultValue = '',
  error = '',
  helperText = '',
  disabled = false,
  required = false,
  fullWidth = false,
  size = 'medium',
  startIcon = null,
  endIcon = null,
  onFocus,
  onBlur,
  onChange,
  className = '',
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(value !== '' || defaultValue !== '');

  const baseClass = 'mexx-input';
  const wrapperClass = 'mexx-input__wrapper';
  const containerClass = 'mexx-input__container';
  const sizeClass = `mexx-input--${size}`;
  const errorClass = error ? 'mexx-input--error' : '';
  const disabledClass = disabled ? 'mexx-input--disabled' : '';
  const focusedClass = isFocused ? 'mexx-input--focused' : '';
  const filledClass = hasValue ? 'mexx-input--filled' : '';
  const fullWidthClass = fullWidth ? 'mexx-input--full-width' : '';

  const inputClass = [
    baseClass,
    sizeClass,
    errorClass,
    disabledClass,
    focusedClass,
    filledClass,
    className
  ].filter(Boolean).join(' ');

  const containerClasses = [
    containerClass,
    sizeClass,
    errorClass,
    disabledClass,
    focusedClass,
    filledClass,
    fullWidthClass
  ].filter(Boolean).join(' ');

  const handleFocus = (e) => {
    setIsFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleChange = (e) => {
    setHasValue(e.target.value !== '');
    if (onChange) {
      onChange(e);
    }
  };

  const inputId = props.id || `mexx-input-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={wrapperClass}>
      {/* Label */}
      {label && (
        <label htmlFor={inputId} className="mexx-input__label">
          {label}
          {required && <span className="mexx-input__required">*</span>}
        </label>
      )}

      {/* Input Container */}
      <div className={containerClasses}>
        {/* Start Icon */}
        {startIcon && (
          <span className="mexx-input__icon mexx-input__icon--start">
            {startIcon}
          </span>
        )}

        {/* Input Field */}
        <input
          ref={ref}
          id={inputId}
          type={type}
          className={inputClass}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          required={required}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          {...props}
        />

        {/* End Icon */}
        {endIcon && (
          <span className="mexx-input__icon mexx-input__icon--end">
            {endIcon}
          </span>
        )}
      </div>

      {/* Helper Text or Error */}
      {(error || helperText) && (
        <div className="mexx-input__feedback">
          {error ? (
            <span className="mexx-input__error-text">
              <svg
                className="mexx-input__error-icon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
                <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
              </svg>
              {error}
            </span>
          ) : (
            <span className="mexx-input__helper-text">{helperText}</span>
          )}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

Input.propTypes = {
  type: PropTypes.oneOf([
    'text',
    'email',
    'password',
    'number',
    'tel',
    'url',
    'search',
    'date',
    'time',
    'datetime-local'
  ]),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  error: PropTypes.string,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  className: PropTypes.string,
  id: PropTypes.string
};

export default Input;